// src/utils/recurrence.js
//
// Authoritative recurrence / due-date service for the whole app.
//
// Every view that needs to know "when is this expense due?" or "which week
// does this date fall in?" must go through these helpers so the Weekly,
// Monthly, Annual, Planner, header and report views can never disagree.
//
// Design rules:
//   • Financial due dates are CALENDAR DATES ("YYYY-MM-DD"), never timestamps.
//     They are parsed manually — `new Date('YYYY-MM-DD')` is banned because it
//     parses as UTC midnight and shifts one day earlier in Canadian timezones.
//   • Weeks are day-of-month buckets, identical everywhere:
//       Week 1 = days 1–7, Week 2 = 8–14, Week 3 = 15–21,
//       Week 4 = 22–28, Week 5 = 29+.
//   • Month-based recurrences that anchor on the 29th/30th/31st clamp to the
//     last day of shorter months (Jan 31 → Feb 28/29 → Mar 31 …).
//   • The anniversary of an annual expense recurs every year on the same
//     month/day (Feb 29 anchors clamp to Feb 28 in non-leap years).

export const RECURRENCE_TYPES = [
  'weekly',
  'bi-weekly',
  'monthly',
  'quarterly',
  'semi-annual',
  'annual',
  'one-time'
];

// Months covered by one step of each month-based recurrence.
const MONTH_STEPS = {
  monthly: 1,
  quarterly: 3,
  'semi-annual': 6,
  annual: 12
};

// Days covered by one step of each day-based recurrence.
const DAY_STEPS = {
  weekly: 7,
  'bi-weekly': 14
};

/**
 * Parse a date-only value into { year, month, day } (month is 1–12).
 * Accepts "YYYY-MM-DD" (canonical) and Date instances (read in local time).
 * Returns null for anything unparseable — callers decide the fallback.
 */
export function parseDateParts(value) {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }
  if (typeof value !== 'string' || !value.trim()) return null;
  const match = value.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  return { year, month, day };
}

/** Local-midnight Date for a "YYYY-MM-DD" string (timezone-safe). */
export function parseLocalDate(value) {
  const parts = parseDateParts(value);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

/** Number of days in a month (month is 1–12). Handles leap years. */
export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/** Clamp a day-of-month to the given month's length (31st → 28/29/30). */
export function clampDayToMonth(year, month, day) {
  return Math.min(day, daysInMonth(year, month));
}

/** Format year/month(1–12)/day as "YYYY-MM-DD". */
export function toDateString(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Week bucket (0–4) for a day of month: 1–7 → 0, 8–14 → 1, 15–21 → 2,
 * 22–28 → 3, 29+ → 4. Returns null for invalid days.
 */
export function getWeekIndexForDay(day) {
  if (!Number.isFinite(day) || day < 1) return null;
  if (day >= 29) return 4;
  return Math.min(4, Math.ceil(day / 7) - 1);
}

/**
 * Week bucket (0–4) for a "YYYY-MM-DD" date, or null if there is no valid
 * date. This is THE week rule — every page must use it.
 */
export function getWeekIndexForDate(value) {
  const parts = parseDateParts(value);
  if (!parts) return null;
  return getWeekIndexForDay(parts.day);
}

/**
 * Effective recurrence type for an expense record.
 * An explicit, valid `frequency`/`recurrenceType` field wins; otherwise
 * expenses stored under Monthly recur monthly and those under Annual recur
 * annually (the app's existing implicit model).
 */
export function getRecurrenceType(expense, isAnnual) {
  const explicit = expense?.recurrenceType || expense?.frequency;
  if (explicit && RECURRENCE_TYPES.includes(explicit)) return explicit;
  return isAnnual ? 'annual' : 'monthly';
}

/** Serial day number (days since epoch, local) for date parts. */
function serialDay({ year, month, day }) {
  return Math.round(new Date(year, month - 1, day).getTime() / 86400000);
}

function partsFromSerial(serial) {
  const d = new Date(serial * 86400000);
  // Round-trips through local time; serials were built the same way.
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function compareParts(a, b) {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

/**
 * All occurrence dates ("YYYY-MM-DD") an expense produces inside an
 * INCLUSIVE date range.
 *
 * @param {Object} expense        Expense record. `date` is the anchor
 *                                (original/next due date). Optional
 *                                `recurrenceType`/`frequency` overrides the
 *                                implicit type.
 * @param {string} rangeStart     "YYYY-MM-DD" inclusive.
 * @param {string} rangeEnd       "YYYY-MM-DD" inclusive.
 * @param {Object} [options]
 * @param {boolean} [options.isAnnual] Collection the expense lives in, used
 *                                when no explicit recurrence type is stored.
 * @returns {string[]} Sorted occurrence dates inside the range. Empty when
 *                     the expense has no valid anchor date.
 */
export function getOccurrencesInRange(expense, rangeStart, rangeEnd, options = {}) {
  const anchor = parseDateParts(expense?.date);
  const start = parseDateParts(rangeStart);
  const end = parseDateParts(rangeEnd);
  if (!anchor || !start || !end || compareParts(start, end) > 0) return [];

  const type = getRecurrenceType(expense, options.isAnnual);
  const occurrences = [];

  if (type === 'one-time') {
    if (compareParts(anchor, start) >= 0 && compareParts(anchor, end) <= 0) {
      occurrences.push(toDateString(anchor.year, anchor.month, anchor.day));
    }
    return occurrences;
  }

  if (DAY_STEPS[type]) {
    const step = DAY_STEPS[type];
    const anchorSerial = serialDay(anchor);
    const startSerial = serialDay(start);
    const endSerial = serialDay(end);
    // First occurrence at or after the range start (recurrences also project
    // backwards so a long-standing anchor date keeps working).
    const k = Math.ceil((startSerial - anchorSerial) / step);
    for (let s = anchorSerial + k * step; s <= endSerial; s += step) {
      const p = partsFromSerial(s);
      occurrences.push(toDateString(p.year, p.month, p.day));
    }
    return occurrences;
  }

  const step = MONTH_STEPS[type] || 1;
  // Walk month-based occurrences from the first candidate month ≥ range start.
  const anchorIndex = anchor.year * 12 + (anchor.month - 1);
  const startIndex = start.year * 12 + (start.month - 1);
  const endIndex = end.year * 12 + (end.month - 1);
  let k = Math.ceil((startIndex - anchorIndex) / step);
  // Include the month before the range start in case the clamped day falls
  // inside the range (cannot actually happen for day-of-month clamping, but
  // starting one step early is cheap and keeps the loop logic simple).
  k -= 1;
  for (let index = anchorIndex + k * step; index <= endIndex; index += step) {
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    const day = clampDayToMonth(year, month, anchor.day);
    const candidate = { year, month, day };
    if (compareParts(candidate, start) < 0 || compareParts(candidate, end) > 0) continue;
    occurrences.push(toDateString(year, month, day));
  }
  return occurrences;
}

/**
 * The single occurrence date an expense produces inside a calendar month,
 * or null when it is not due that month.
 *
 * @param {Object} expense   Expense record.
 * @param {number} year      Full year, e.g. 2026.
 * @param {number} month0    0-based month (JavaScript convention), 0–11.
 * @param {Object} [options] { isAnnual }
 * @returns {string|null} "YYYY-MM-DD" of the occurrence, or null.
 */
export function getOccurrenceInMonth(expense, year, month0, options = {}) {
  const month = month0 + 1;
  const start = toDateString(year, month, 1);
  const end = toDateString(year, month, daysInMonth(year, month));
  const occurrences = getOccurrencesInRange(expense, start, end, options);
  return occurrences.length > 0 ? occurrences[0] : null;
}

/** True when the expense produces an occurrence inside the calendar month. */
export function occursInMonth(expense, year, month0, options = {}) {
  return getOccurrenceInMonth(expense, year, month0, options) !== null;
}

/**
 * The active budget month/year for the app: derived from the first income
 * source with pay dates (the app's long-standing rule), falling back to
 * today. Returns { year, month } with a 0-based month.
 */
export function getBudgetMonthYear(income, today = new Date()) {
  for (const source of Array.isArray(income) ? income : []) {
    if (Array.isArray(source?.payDates) && source.payDates.length > 0) {
      const parts = parseDateParts(source.payDates[0]);
      if (parts) return { year: parts.year, month: parts.month - 1 };
    }
  }
  return { year: today.getFullYear(), month: today.getMonth() };
}
